import { z } from 'zod';
/**
 * A drivers'-hours ruleset the CUSTOMER defines (AjnaView Q6, answered from our side).
 *
 * ⚠️ **THE PLATFORM SHIPS NO REGULATORY FIGURES HERE, and that is the design rather than an
 * omission.** `tachograph-parameters.md` records the rule: the drivers'-hours limits live in the
 * seed script and nowhere in `packages/`, because a real tachograph applies a ruleset and wanting
 * those constants in the platform is the signal to stop. So these numbers are the customer's
 * statement of what governs a vehicle, and nothing downstream calls a result an infringement.
 *
 * ⚠️ **Only the five figures a REGULATION determines.** `breakWarnWithinMin` and
 * `restWarnWithinMin` stay on the tenant's own configuration: they say how close to the DEVICE'S
 * remaining-time counter counts as "approaching", which is identical under assimilated/AETR and
 * GB domestic alike. Five of the seven drivers'-hours alerts never read a ruleset at all.
 */
const limits = {
    /** Minutes of daily driving at which to warn. */
    dailyWarnMin: z.number().int().min(60).max(900),
    /** The daily limit itself. */
    dailyLimitMin: z.number().int().min(60).max(900),
    /** The extended maximum, where the ruleset permits one. */
    dailyMaxMin: z.number().int().min(60).max(900),
    weeklyWarnMin: z.number().int().min(600).max(6000),
    weeklyLimitMin: z.number().int().min(600).max(6000),
};
export const driversHoursRulesetSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    ...limits,
    /** Archived rulesets keep governing the vehicles already assigned to them. */
    archived: z.boolean(),
    /** How many vehicles this ruleset currently governs. */
    vehicleCount: z.number().int().min(0),
});
export const driversHoursRulesetListSchema = z.object({
    items: z.array(driversHoursRulesetSchema),
    /**
     * The tenant's own configured limits — what a vehicle with NO ruleset is judged by.
     *
     * ⚠️ Returned with the list because the screen has to say what "no ruleset" MEANS. A picker
     * whose empty option said only "None" would read as *no limits*, which is the opposite of what
     * an absent ruleset does, and is the single most dangerous misreading available here.
     */
    tenantDefault: z.object(limits),
});
/**
 * ⚠️ **ORDERED, not merely in range** — mirrored from the database CHECKs so the refusal arrives
 * with a sentence rather than a constraint name. A warn above a limit, or a limit above the
 * extended maximum, is a ruleset that can never warn before it breaches: silent, because every
 * number is individually plausible.
 */
const ordered = (schema) => schema
    .refine((v) => v.dailyWarnMin === undefined ||
    v.dailyLimitMin === undefined ||
    v.dailyWarnMin <= v.dailyLimitMin, { message: 'The daily warning must not be above the daily limit', path: ['dailyWarnMin'] })
    .refine((v) => v.dailyLimitMin === undefined ||
    v.dailyMaxMin === undefined ||
    v.dailyLimitMin <= v.dailyMaxMin, { message: 'The daily limit must not be above the extended maximum', path: ['dailyMaxMin'] })
    .refine((v) => v.weeklyWarnMin === undefined ||
    v.weeklyLimitMin === undefined ||
    v.weeklyWarnMin <= v.weeklyLimitMin, { message: 'The weekly warning must not be above the weekly limit', path: ['weeklyWarnMin'] });
export const createDriversHoursRulesetSchema = ordered(z
    .object({
    name: z.string().trim().min(2).max(60),
    description: z.string().trim().max(240).optional(),
    ...limits,
})
    .strict());
/**
 * ⚠️ Every field optional, and an OMITTED field is unchanged — the distinction the AI allowance
 * already records. Collapsing omitted with null would reset a limit whenever somebody edited a
 * different one.
 */
export const updateDriversHoursRulesetSchema = ordered(z
    .object({
    name: z.string().trim().min(2).max(60).optional(),
    description: z.string().trim().max(240).nullable().optional(),
    dailyWarnMin: limits.dailyWarnMin.optional(),
    dailyLimitMin: limits.dailyLimitMin.optional(),
    dailyMaxMin: limits.dailyMaxMin.optional(),
    weeklyWarnMin: limits.weeklyWarnMin.optional(),
    weeklyLimitMin: limits.weeklyLimitMin.optional(),
})
    .strict());
//# sourceMappingURL=drivers-hours-ruleset.js.map